import type { Diagnostic } from "@codemirror/lint";
import type { EditorState } from "@codemirror/state";

// The rows of an editor's Errors popover (Figma ErrorList 15067-167750):
// the error-severity diagnostics of the last lint pass as 1-based
// line/position readouts, in document order. Warnings (the pipeline
// editor's placeholder marks) stay out — the list is titled "Errors".

export interface EditorErrorRow {
  line: number;
  col: number;
  message: string;
  /** Document offset the row jumps the cursor to. */
  from: number;
}

// JSON.parse's messages end in "in JSON at position N (line L column C)"
// (wording varies by engine) — the row states the position itself.
const JSON_POSITION_SUFFIX =
  / in JSON at position \d+( \(line \d+ column \d+\))?$/;

function rowMessage(message: string): string {
  return message.replace(JSON_POSITION_SUFFIX, "");
}

// Where JSON.parse's SyntaxError points (jsonParseLinter's rule): the
// "at position N" offset (V8), or "line L column C" (SpiderMonkey); an
// unlocated error ("Unexpected end of JSON input") sits at the end.
function jsonErrorOffset(message: string, text: string): number {
  const atPosition = message.match(/at position (\d+)/);
  if (atPosition) return Math.min(Number(atPosition[1]), text.length);
  const atLine = message.match(/line (\d+) column (\d+)/);
  if (!atLine) return text.length;
  const lines = text.split("\n");
  const before = lines.slice(0, Number(atLine[1]) - 1);
  const lineStart = before.reduce((n, l) => n + l.length + 1, 0);
  return Math.min(lineStart + Number(atLine[2]) - 1, text.length);
}

function lineColAt(
  text: string,
  offset: number,
): { line: number; col: number } {
  const before = text.slice(0, offset);
  const lastBreak = before.lastIndexOf("\n");
  return {
    line: before.split("\n").length,
    col: offset - lastBreak,
  };
}

/** The JSON editors' rows, straight from JSON.parse — synchronous, so the
 *  status bar's count and its red state come from the same parse (the
 *  squiggles come from the linter, on its own delay). */
export function jsonErrorRows(text: string): EditorErrorRow[] {
  if (text === "") return [];
  try {
    JSON.parse(text);
    return [];
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;
    const from = jsonErrorOffset(error.message, text);
    return [
      { ...lineColAt(text, from), message: rowMessage(error.message), from },
    ];
  }
}

/** Why a schema text can't be saved, or deployed as stored: it is empty
 *  (whitespace only) or it isn't valid JSON — null when it is fine. The
 *  JSON editors' zero state exempts "" from jsonErrorRows; a stored
 *  schema with no definition is not a schema, so here it counts. */
export function schemaTextProblem(text: string): "empty" | "invalid" | null {
  if (text.trim() === "") return "empty";
  return jsonErrorRows(text).length > 0 ? "invalid" : null;
}

export function errorRows(
  state: EditorState,
  diagnostics: readonly Diagnostic[],
): EditorErrorRow[] {
  return diagnostics
    .filter((d) => d.severity === "error")
    .map((d) => {
      const from = Math.min(d.from, state.doc.length);
      const line = state.doc.lineAt(from);
      return {
        line: line.number,
        col: from - line.from + 1,
        message: rowMessage(d.message),
        from,
      };
    })
    .sort((a, b) => a.from - b.from);
}

/** The status bar button's text (Figma Bridge_PipelineCode 14785-265837). */
export function errorCountLabel(count: number): string {
  return count === 1 ? "1 error" : `${count} errors`;
}

export function errorRowLabel(row: EditorErrorRow): string {
  return `line ${row.line}, position ${row.col}: ${row.message}`;
}

// The message "Fix with AI" sends to the helper, as if the user typed it.
// With `code`, the document's text rides along in a fenced block: the
// pipeline code's structured context item exists only while the code
// parses (and keeps a subset of its keys), so a fix request for the code
// must carry the code itself.
export function fixWithAiMessage(
  documentName: string,
  rows: readonly EditorErrorRow[],
  code?: string,
): string {
  const lines = rows.map((row) => `- ${errorRowLabel(row)}`);
  const fence = code === undefined ? [] : ["", "```json", code, "```"];
  return [`Fix the errors in the ${documentName}:`, ...lines, ...fence].join(
    "\n",
  );
}
